export type CpicStatus = 'unmapped' | 'pending_review' | 'promoted' | 'not_relevant';

export type CpicRec = {
  drugname?: string;
  phenotypes?: Record<string, string>;
  classification?: string;
  drugrecommendation?: string;
  guideline_name?: string;
};

export type CpicSeverity = 'critical' | 'high' | 'moderate' | 'routine';

export type CpicCandidate = {
  candidate_id: string;
  translation_status: CpicStatus;
  reviewer?: string | null;
  reviewed_at?: string | null;
  promoted_entry_id?: string | null;
  severity?: CpicSeverity;
  cpic: CpicRec;
  proposed_safety_entry?: Record<string, unknown>;
  translation_notes?: string;
};

export type CpicCandidatesResponse = {
  present: boolean;
  derived_at?: string;
  snapshot_fetch_tag?: string;
  candidate_count?: number;
  severity_counts?: Partial<Record<CpicSeverity, number>>;
  candidates: CpicCandidate[];
};

export const CPIC_STATUSES: CpicStatus[] = ['unmapped', 'pending_review', 'promoted', 'not_relevant'];

export const CPIC_STATUS_HEX: Record<CpicStatus, string> = {
  unmapped: '#8A94A6', pending_review: '#F2A93B', promoted: '#2BBAA8', not_relevant: '#8A94A6',
};

// Highest-stakes first; matches the backend severity tiers.
export const CPIC_SEVERITIES: CpicSeverity[] = ['critical', 'high', 'moderate', 'routine'];

export const CPIC_SEVERITY_HEX: Record<CpicSeverity, string> = {
  critical: '#F2635A', high: '#F2A93B', moderate: '#5B72E8', routine: '#8A94A6',
};

export function genePhenotype(c: CpicCandidate): string {
  const p = c.cpic?.phenotypes ?? {};
  return Object.entries(p).map(([g, ph]) => `${g}: ${ph}`).join(', ') || '—';
}

/** The drug pre-filled as the compound on the proposed entry (single-gene at the
 *  top level, multi-gene as the first effect). Empty when not yet resolved. */
export function proposedCompound(c: CpicCandidate): string {
  const e = c.proposed_safety_entry as Record<string, unknown> | undefined;
  if (!e) return '';
  if (typeof e.compound === 'string' && e.compound) return e.compound;
  const effects = e.compound_effects;
  if (Array.isArray(effects) && effects.length) {
    const first = effects[0] as Record<string, unknown>;
    if (typeof first?.compound === 'string') return first.compound;
  }
  return '';
}

export function filterCpic(
  list: CpicCandidate[], query: string, status: string, severity = '',
): CpicCandidate[] {
  const q = query.toLowerCase().trim();
  return list.filter((c) => {
    if (status && c.translation_status !== status) return false;
    if (severity && (c.severity ?? '') !== severity) return false;
    if (!q) return true;
    return (c.cpic?.drugname || '').toLowerCase().includes(q) ||
      genePhenotype(c).toLowerCase().includes(q) ||
      c.candidate_id.toLowerCase().includes(q);
  });
}
